import type { MarketingProductListResponse } from '../../types';
import type {
  StyleHotProductCategoryProduct,
  StyleHotProductRawAdCategory,
  StyleHotProductRawCategoryTab,
  StyleHotProductStyleQuizData,
  StyleHotProductStyleName,
  StyleHotProductStyleQuizQuestion,
  StyleHotProductStyleQuizResult,
} from './types';

export const STYLE_CATEGORY_PRODUCT_PAGE_SIZE = 50;

const STYLE_SCORE_PRIORITY: StyleHotProductStyleName[] = [
  'Bold',
  'Elegant',
  'Retro',
  'Business',
  'Clean',
];

type StyleQuizAnswerSelectionMap = Record<number, string>;

function parseQuestionWeight(questionWeight: string) {
  const weight = Number(questionWeight.replace('%', ''));

  return Number.isFinite(weight) ? weight / 100 : 0;
}

export function calculateMatchedStyleTitle(
  questions: StyleHotProductStyleQuizQuestion[],
  answers: StyleQuizAnswerSelectionMap,
  results: StyleHotProductStyleQuizResult[],
) {
  const scores = STYLE_SCORE_PRIORITY.reduce(
    (styleScores, styleName) => ({
      ...styleScores,
      [styleName]: 0,
    }),
    {} as Record<StyleHotProductStyleName, number>,
  );

  questions.forEach((question) => {
    const selectedAnswerId = answers[question.question_id];
    const selectedAnswer = question.answers.find((answer) => answer.answer_id === selectedAnswerId);

    if (!selectedAnswer) {
      return;
    }

    const questionWeight = parseQuestionWeight(question.question_Weight);

    STYLE_SCORE_PRIORITY.forEach((styleName) => {
      scores[styleName] += Number(selectedAnswer.style_weight?.[styleName] || 0) * questionWeight;
    });
  });

  const matchedStyleIndex = STYLE_SCORE_PRIORITY.reduce((matchedIndex, styleName, index) => {
    return scores[styleName] > scores[STYLE_SCORE_PRIORITY[matchedIndex]] ? index : matchedIndex;
  }, 0);

  // result 按五种风格的固定顺序提供各站点本地化后的展示内容。
  return results[matchedStyleIndex]?.style_title || results[0]?.style_title || '';
}
// 风格测试结果判断用pc图片还是移动端图片
export function resolveStyleQuizResultImageSrc(
  result: StyleHotProductStyleQuizResult | undefined,
  isWeb: boolean,
): string | undefined {
  if (!result) {
    return undefined;
  }

  return isWeb ? result.style_img : result.style_img_wap || result.style_img;
}

export function resolveStyleQuizWelcomeImageSrc(
  tipsText: StyleHotProductStyleQuizData['tipsText'] | undefined,
  isWeb: boolean,
): string | undefined {
  if (!tipsText) {
    return undefined;
  }

  return isWeb
    ? tipsText.welcome_page_bg
    : tipsText.welcome_page_bg_wap || tipsText.welcome_page_bg;
}

export function getDefaultStyleCategoryTabIndex(
  tabs?: StyleHotProductRawAdCategory['tabs'],
): number {
  return Array.isArray(tabs) && tabs.length > 2 ? 2 : 0;
}

export function getInitialStyleCategoryTabIndex(
  styleCategory?: StyleHotProductRawAdCategory | null,
): number {
  const tabs = styleCategory?.tabs;
  const rawActiveTabIndex = styleCategory?.active_tab_index;
  const activeTabIndex =
    rawActiveTabIndex === null || rawActiveTabIndex === undefined || rawActiveTabIndex === ''
      ? NaN
      : Number(rawActiveTabIndex);

  if (
    Array.isArray(tabs) &&
    Number.isInteger(activeTabIndex) &&
    activeTabIndex >= 0 &&
    activeTabIndex < tabs.length
  ) {
    return activeTabIndex;
  }

  return getDefaultStyleCategoryTabIndex(tabs);
}

export function getMarketingProductListItems(
  response?: MarketingProductListResponse | null,
): StyleHotProductCategoryProduct[] {
  if (Array.isArray(response?.items)) {
    return response.items as StyleHotProductCategoryProduct[];
  }

  if (Array.isArray(response?.list)) {
    return response.list as StyleHotProductCategoryProduct[];
  }

  return [];
}

export function hasLoadedStyleCategoryProducts(tab?: StyleHotProductRawCategoryTab): boolean {
  return Boolean(
    tab?.products?.some(
      (product) =>
        product?.shop_sku ||
        product?.name ||
        product?.short_name ||
        product?.image ||
        product?.correlation,
    ),
  );
}

export function mergeStyleCategoryTabProducts(
  tab: StyleHotProductRawCategoryTab,
  products: StyleHotProductCategoryProduct[],
): StyleHotProductRawCategoryTab {
  const configuredProducts = Array.isArray(tab.products) ? tab.products : [];
  const configById = new Map(
    configuredProducts
      .filter((product) => product?.id)
      .map((product) => [Number(product.id), product]),
  );

  return {
    ...tab,
    products: products.map((product, index) => {
      const configuredProduct = configById.get(Number(product.id)) || configuredProducts[index];

      return configuredProduct ? { ...product, ...configuredProduct } : product;
    }),
  };
}

export function mergeStyleCategoryProductsByTabIndex(
  styleCategory: StyleHotProductRawAdCategory | null | undefined,
  tabIndex: number,
  products: StyleHotProductCategoryProduct[],
): StyleHotProductRawAdCategory | null | undefined {
  const tabs = styleCategory?.tabs;

  if (!Array.isArray(tabs) || !tabs[tabIndex]) {
    return styleCategory;
  }

  return {
    ...styleCategory,
    tabs: tabs.map((tab, index) =>
      index === tabIndex ? mergeStyleCategoryTabProducts(tab, products) : tab,
    ),
  };
}
